import React, { useCallback, useContext, useEffect, useRef, useState, type FC } from 'react';
import { Table, TableCell, TableHead, TableRow } from '@mui/material';
import { t } from 'i18next';
import styles from './usersTable.module.scss';
import ObrasAPI from '../../../services/APIObras';
import UsersTableRow from './usersTableRow/usersTableRow';
import { AdminUsersPage, Usuario } from '../../../types/Admin';
import LoadingSpinner from '../../../components/loadingSpinner/loadingSpinner';
import { AdminContext } from '../../../context/AdminProvider';

interface UsersTableProps {
  userInput: string;
  canManageUserRoles: boolean;
}

const UsersTable: FC<UsersTableProps> = (props) => {
  const { userInput, canManageUserRoles } = props;
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<Usuario[]>([]);
  const [query, setQuery] = useState({ page: 0, search: userInput.trim() });
  const api = ObrasAPI.getInstance();
  const observer = useRef<IntersectionObserver>();
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const adminContext = useContext(AdminContext);
  const { usersRefreshVersion } = adminContext;

  const lastUserRef = useCallback((user: HTMLDivElement) => {
    if (observer.current) observer.current.disconnect();
    if (!user || loadingRef.current || !hasMoreRef.current) return;

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !loadingRef.current && hasMoreRef.current) {
        setQuery(previous => ({ ...previous, page: previous.page + 1 }));
      }
    });
    observer.current.observe(user);
  }, []);

  useEffect(() => {
    const normalizedSearch = userInput.trim();
    if (normalizedSearch === query.search) return undefined;

    const timeout = window.setTimeout(() => {
      setQuery({ page: 0, search: normalizedSearch });
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [userInput, query.search]);

  useEffect(() => {
    let cancelled = false;
    if (query.page === 0) hasMoreRef.current = true;
    loadingRef.current = true;
    setLoading(true);

    api.getUsers(query.page, query.search)
      .then(response => {
        if (cancelled) return;
        if (!('data' in response) || !Array.isArray(response.data)) {
          if (query.page === 0) setUsers([]);
          hasMoreRef.current = false;
          return;
        }

        const page = response as AdminUsersPage;
        adminContext.handleCacheUsers(page.data);
        setUsers(previous => query.page === 0 ? page.data : [...previous, ...page.data]);
        hasMoreRef.current = page.hasNextPage;
      })
      .catch(error => {
        if (!cancelled) {
          console.error('Unable to load AuthZ users', error);
          if (query.page === 0) setUsers([]);
          hasMoreRef.current = false;
        }
      })
      .finally(() => {
        if (!cancelled) {
          loadingRef.current = false;
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // AdminContext handlers are intentionally excluded: caching a page updates the context.
  }, [query]);

  useEffect(() => {
    setUsers([]);
    hasMoreRef.current = true;
    setQuery(previous => ({ ...previous, page: 0 }));
  }, [usersRefreshVersion]);

  useEffect(() => () => observer.current?.disconnect(), []);

  return (
    <div className={styles.usersTable}>
      <Table>
        <TableHead>
          <TableRow className={styles.tableHead}>
            <TableCell>{t('administration.table.name')}</TableCell>
            <TableCell>{t('administration.table.roles')}</TableCell>
            <TableCell align='right'>{t('administration.table.edit')}</TableCell>
          </TableRow>
        </TableHead>
      </Table>
      {users.length > 0 ?
        <div className={styles.tableBody}>
          {users.map((u, i) => {
            return (
              <div
                key={u.id}
                className={styles.tableRowReference}
                ref={i === users.length - 1 ? lastUserRef : undefined}
              >
                <UsersTableRow
                  userInput={userInput}
                  user={u}
                  index={i}
                  canManageUserRoles={canManageUserRoles}
                />
              </div>
            );
          })}
          {loading && <LoadingSpinner />}
        </div>
        : loading ?
          <div className={styles.tableBody} style={{ height: '300px' }}>
            <LoadingSpinner />
          </div> :
          <div className={styles.tableBody} style={{
            height: '82px',
            display: 'flex',
            flexFlow: 'row',
            marginTop: '20px',
            width: '100%',
            marginLeft: '15px',
          }}>
            <p>{t('administration.table.notFound')}</p>
          </div>
      }
    </div>
  );
};

export default UsersTable;
